/* eslint-disable */
import React, { FC } from 'react';
import { Row, Col, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useDirection } from '~/hooks/i18nHooks';
import { useIsMobile } from '~/hooks/useScreenSizes';
import { PricingServiceField, ServiceField } from '~/types/Services';
import { PricingValue } from '~/types/PricingValue';
import { SaudiReyalSymbolIcon } from '~/img';

interface Props {
  fields: ServiceField[];
  /**
   * compact=true  → small inline style used inside the service card modal
   *   - description shown inline in parens after the label: "Label (description)"
   *   - amountCap shown inline after value: "0.8% (capped at ﷼160)"
   * compact=false → full-size style used inside the details / drawer view
   *   - description shown as a separate gray subtitle line below the label
   *   - amountCap shown as a separate gray line below the value
   */
  compact?: boolean;
}

/** Structured result from formatPricingValue */
interface FormattedValue {
  /** Main value node: percent, amount, or "Waived" */
  main: React.ReactNode;
  /** Cap node (icon + number only, no label) – null when there is no cap */
  cap: React.ReactNode | null;
}

/**
 * Returns main value and cap separately so the caller can decide
 * whether to render them inline (compact) or stacked (full).
 */
function formatPricingValue(
  value: PricingValue,
  t: (key: string) => string,
  SaudiIcon: React.FC<any>,
  dir?: string,
  /** When true and in RTL mode, renders SAR amount before percent (﷼1 + 2.5%).
   *  Pass this based on field identity — NOT on the current values — so the
   *  display intent stays stable even if the API data changes in future. */
  swapRtlOrder?: boolean
): FormattedValue {
  const { amount, percent, amountCap } = value;

  // amount = 0 and no percent → Waived
  if (amount !== undefined && amount.value === 0 && percent === undefined) {
    return { main: <span>{t('Waived')}</span>, cap: null };
  }

  const isRtl = dir === 'rtl';

  const amtSpan = amount !== undefined && amount.value > 0 ? (
    // .sarUnit: inline-flex LTR isolated unit keeps icon before number in bidi context
    <span key="amt" className="sarUnit">
      <SaudiIcon className="feeIcon" />
      {amount.value}
    </span>
  ) : null;

  const mainParts: React.ReactNode[] = [];

  if (isRtl && swapRtlOrder && amtSpan && percent !== undefined && percent > 0) {
    // Arabic RTL swap (e.g. "Credit card per transaction"):
    // show ﷼1 + 2.5% instead of 2.5% + ﷼1.
    // Guard (amtSpan && percent > 0) ensures both parts actually exist;
    // if API data changes to a single component, falls through to the default.
    mainParts.push(amtSpan);
    mainParts.push(<span key="sep"> + </span>);
    mainParts.push(<span key="pct">{percent}%</span>);
  } else {
    // Default: 2.5%  /  ﷼1  /  2.5% + ﷼1  (LTR order)
    if (percent !== undefined && percent > 0) {
      mainParts.push(<span key="pct">{percent}%</span>);
    }
    if (amtSpan) {
      if (mainParts.length > 0) mainParts.push(<span key="sep"> + </span>);
      mainParts.push(amtSpan);
    }
  }

  // Cap returned separately so caller renders it inline or stacked
  const capNode =
    amountCap !== undefined && amountCap.value > 0 ? (
      // .sarUnit: same isolation keeps "﷼160" as a unit after Arabic cap-note text
      <span className="sarUnit">
        <SaudiIcon className="feeIcon" />
        {amountCap.value}
      </span>
    ) : null;

  return { main: <>{mainParts}</>, cap: capNode };
}

/** Replace {SAR} token (or literal ﷼ U+FDFC) in a label string with the Saudi Riyal SVG icon.
 *
 * Arabic i18n translations embed the ﷼ character directly instead of using
 * the {SAR} placeholder — so we normalise both to the same path first.
 *
 * RTL handling: the ﷼ character (U+FDFC, strongly RTL) inside an RTL paragraph
 * causes bidi to place the following LTR number visually to its LEFT, giving
 * "100 ﷼" instead of "﷼100".  We fix this by wrapping [icon + leading digits]
 * together in a single isolated LTR span — they become one bidi-neutral unit
 * whose internal order is left-to-right (icon before number). */
function renderLabelWithIcon(
  label: string,
  SaudiIcon: React.FC<any>,
  dir?: string
): React.ReactNode {
  // Normalise: replace literal ﷼ (U+FDFC) → {SAR} so both translation formats
  // ('{SAR}100' and '﷼100') are handled by the same icon-rendering code below.
  const normalized = label.replace(/\uFDFC/g, '{SAR}');
  if (!normalized.includes('{SAR}')) return label;
  const isRtl = dir === 'rtl';
  const parts = normalized.split('{SAR}');
  return (
    <>
      {parts.map((part, i) => {
        if (i === parts.length - 1) {
          // Last segment — no icon after it
          return <React.Fragment key={i}>{part}</React.Fragment>;
        }

        if (isRtl) {
          // RTL: bundle [icon + leading number] as one isolated LTR unit so
          // the digits don't drift visually left of the icon.
          const nextPart = parts[i + 1];
          const numMatch = nextPart.match(/^[\d,./]+/);
          const leadingNum = numMatch ? numMatch[0] : '';
          // We consume the leading number here; the rest of nextPart will be
          // rendered by the i+1 iteration (minus the leading number we took).
          parts[i + 1] = nextPart.slice(leadingNum.length);
          return (
            <React.Fragment key={i}>
              {part}
              {/* .sarUnit: bundles [icon + digits] as one LTR unit so
                  digits don't drift left of the icon in RTL context */}
              <span className="sarUnit">
                <SaudiIcon className="feeIcon" />
                {leadingNum}
              </span>
            </React.Fragment>
          );
        }

        // LTR: plain icon at the {SAR} position
        return (
          <React.Fragment key={i}>
            {part}
            <SaudiIcon className="feeIcon" />
          </React.Fragment>
        );
      })}
    </>
  );
}

/**
 * Renders the Fees & Charges table for a service from the API field definitions.
 *
 * Pass `compact` when embedding inside the service card (small, tight layout).
 * Omit `compact` (or pass false) for the full-size modal / drawer layout.
 */
const PosFeesAndCharges: FC<Props> = ({ fields, compact = false }) => {
  const { t } = useTranslation(['ksaApplication', 'common']);
  const dir = useDirection();
  const isMobile = useIsMobile();

  const feeFields = fields.filter(
    (f) =>
      (f.tag === 'FEES_AND_CHARGES' ||
        f.key === 'setupFee' ||
        f.key === 'setupFees' ||
        f.key.toLowerCase().includes('mada')) &&
      f.type === 'pricing' &&
      // skip vat (shown as "All prices are VAT excluded" footer)
      f.key !== 'vat' &&
      // skip terminalFee (it's the per-device fee, not shown in the card summary)
      f.key !== 'terminalFee'
  ) as PricingServiceField[];

  // Keys in the order they should appear — both setupFee and setupFees variants pinned first
  const feeOrder = [
    'setupFee',
    'setupFees',
    'monthlyFee',
    'madaPricing', // E-commerce mada key from API
    'mada',
    'madaFee',
    'madaLessThan100',
    'madaEqualOrAbove100',
    'creditcard',
    'gcc',
    'minimumUsageFee',
    'fundsTransferFee',
  ];

  const sortedFeeFields = [...feeFields].sort((a, b) => {
    const indexA = feeOrder.indexOf(a.key);
    const indexB = feeOrder.indexOf(b.key);
    const orderA = indexA !== -1 ? indexA : 999;
    const orderB = indexB !== -1 ? indexB : 999;
    return orderA - orderB;
  });

  return (
    <FeesContainer dir={dir} compact={compact}>
      <Typography.Title level={compact ? 5 : 3} className="feesTitle">
        {t('Fees & Charges')}
      </Typography.Title>

      {sortedFeeFields.map((field) => {
        const value = field.value.customValue || field.value.defaultValue;
        const label = t(field.name);
        const description = field.description ? t(field.description) : undefined;
        const { main, cap } = formatPricingValue(
          value,
          t,
          SaudiReyalSymbolIcon as any,
          dir,
          // Swap order in RTL for credit card: show ﷼1 + 2.5% (not 2.5% + ﷼1).
          // Tied to field identity, not to the current values, so it stays
          // correct even if the API pricing structure changes in future.
          field.key === 'creditcard'
        );

        return (
          <Row key={field.key} justify="space-between" className="feeRow" gutter={[8, 0]}>

            {/* ── Label column ── */}
            <Col span={14} className="feeLabel">
              {compact ? (
                /*
                 * compact: label + description run inline so they word-wrap together.
                 */
                <div className="feeLabelStack">
                  {renderLabelWithIcon(label, SaudiReyalSymbolIcon as any, dir)}
                  {description &&
                    (isMobile ? (
                      <span className="feeDescription">
                        {renderLabelWithIcon(description, SaudiReyalSymbolIcon as any, dir)}
                      </span>
                    ) : (
                      <span className="feeDescriptionInline">
                        {" ("}
                        {renderLabelWithIcon(description, SaudiReyalSymbolIcon as any, dir)}
                        {")"}
                      </span>
                    ))}
                </div>
              ) : (
                /*
                 * full: label on top, description below.
                 */
                <div className="feeLabelStack">
                  <span>{renderLabelWithIcon(label, SaudiReyalSymbolIcon as any, dir)}</span>
                  {description && (
                    <span className="feeDescription">
                      {renderLabelWithIcon(description, SaudiReyalSymbolIcon as any, dir)}
                    </span>
                  )}
                </div>
              )}
            </Col>

            {/* ── Value column ── */}
            <Col span={10} className="feeValue">
              {compact ? (

                <span>
                  {main}
                  {cap && (
                    <span className="feeCap">
                      {dir === 'rtl'
                        ? <>{' '}{t('capped at')} {cap}</>
                        : <>{' '}({t('capped at')} {cap})</>
                      }
                    </span>
                  )}
                </span>
              ) : (

                <div className="feeValueStack">
                  <span>{main}</span>
                  {cap && (
                    <span className="feeCapBlock">
                      {t('capped at')} {cap}
                    </span>
                  )}
                </div>
              )}
            </Col>

          </Row>
        );
      })}

      <Typography.Text className="vatNote">{t('All prices are VAT excluded')}</Typography.Text>
    </FeesContainer>
  );
};

/* ─── Styled components ───────────────────────────────────────── */

interface ContainerProps {
  dir?: string;
  compact?: boolean;
}

const FeesContainer = styled.div<ContainerProps>`
  width: 100%;
  text-align: ${({ dir }) => (dir === 'rtl' ? 'right' : 'left')};
  direction: ${({ dir }) => dir ?? 'ltr'};
  font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;

  .feesTitle {
    font-weight: 500 !important;
    font-size: ${({ compact }) => (compact ? '18px' : '16px')} !important;
    margin-bottom: ${({ compact }) => (compact ? '8px' : '16px')} !important;
  }

  /* ── Row ── */
  .feeRow {
    display: flex;
    align-items: flex-start;
    margin-bottom: ${({ compact }) => (compact ? '10px' : '14px')};
  }

  /* ── Label column ── */
  .feeLabel {
    display: flex;
    flex-direction: column;
    gap: 2px;

    span {
      color: #000;
      font-family: inter, sans-serif;
      font-size: 14px;
      font-style: normal;
      font-weight: 325;
      line-height: 160%;
      letter-spacing: 0.14px;
    }
  }

  /* web: description inline; mobile: drops to block line below */
  .feeLabelStack {
    display: inline;
  font-family: saudi_riyal, sans-serif;
    @media (max-width: 768px) {
      display: block;
    }
  }

  /* compact: description runs inline after label */
  .feeDescriptionInline {
  font-family: saudi_riyal, sans-serif;
    color: #888;
    font-weight: 400;
  }

  /* full: description as separate gray subtitle */
  .feeDescription {
    display: block;
    color: #8f8f8f;
    font-family: saudi_riyal, sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.12px;
    margin-top: 2px;
  }

  /* ── Value column ── */
  .feeValue {
    color: #070707;
    text-align: ${({ dir }) => (dir === 'rtl' ? 'left !important' : 'right !important')};
    font-family: saudi_riyal, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px;
    letter-spacing: 0.14px;
    /* Force LTR so math expressions like "2.5% + ﷼1" keep natural order in RTL layout */
    // direction: ltr;
  }

  /* full mode: stack main value + cap vertically */
  .feeValueStack {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    font-family: saudi_riyal, sans-serif;
    gap: 2px;
  }

  /* compact: cap inline in parens after main value */
  .feeCap {
    font-weight: 400;
    white-space: nowrap;
  }

  /* full: cap as separate gray subtitle below main value */
  .feeCapBlock {
    display: block;
    font-size: 12px;
    color: #888;
    font-weight: 400;
    white-space: nowrap;
  }

  /* Isolated LTR unit: keeps ﷼ icon before its number in any bidi context.
   * Used by amtSpan, capNode, and renderLabelWithIcon RTL wrapping.
   * unicode-bidi:isolate makes the whole span a neutral atom in the outer
   * bidi algorithm, preventing the strongly-RTL ﷼ char from being absorbed
   * into a surrounding Arabic RTL run. */
  .sarUnit {
    display: inline-flex;
    align-items: center;
    direction: ltr;
    unicode-bidi: isolate;
    white-space: nowrap;
    gap: 4px;
  }

  /* Saudi riyal icon */
  .feeIcon {
    display: inline-block;
    color: #070707;
    font-family: saudi_riyal, sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0.16px;
    vertical-align: middle;
    margin-bottom: 2px;
  }

  .vatNote {
    display: block;
    margin-top: ${({ compact }) => (compact ? '6px' : '11px')};
    font-size: ${({ compact }) => (compact ? '12px' : '16px')};
    color: #4A4A4A;
    font-family: inter;
    font-style: normal;
    font-weight: 325;
    line-height: 160%;
    letter-spacing: 0.14px;
  }
`;

export default PosFeesAndCharges;
